<template>
    <div
        class="JsonSchemaTextForm"
        :class="{'JsonSchemaTextForm--focus': focused}"
    >
        <FormElement
            :label-for="id"
            :label="form.schema.title"
            :required="requiredElement"
        >
            <template #requiredIndicator>
                {{ translate('requiredLabel') }}
            </template>
            <template #labelHint>
                <div v-if="form.schema.warning" class="JsonSchemaForm__warning">
                    {{ form.schema.warning }}
                </div>
                <div v-else-if="form.schema.hint" class="JsonSchemaForm__hint">
                    {{ form.schema.hint }}
                </div>
                <div v-else-if="focused" class="JsonSchemaForm__typeLabel" :class="TypeLabelClass">
                    <FontAwesomeIcon :icon="faExclamationCircle" />
                    <span v-if="form.schema.format === 'url'">URL only</span>
                    <span v-else>Text only</span>
                </div>
                <EidIcon
                    v-else-if="form.schema.description && !rbacDisabled"
                    v-tooltip="{ content: form.schema.description }"
                    class="text-eidQualityBlack-50"
                    alt="description"
                    :icon-src="faQuestionCircle"
                />
            </template>
            <FormInput :invalid="accessed && hasError" force-error-slot force-action-slot>
                <div
                    ref="valueOnly"
                    class="JsonSchemaForm__valueOnly"
                    :class="{'JsonSchemaForm__valueOnly--disabled': disabled || rbacDisabled}"
                    :style="valueOnlyStyle"
                >
                    {{ viewValue }}
                </div>
                <input
                    :id="id"
                    ref="input"
                    :style="inputStyle"
                    type="text"
                    :disabled="disabled || rbacDisabled"
                    :value="viewValue"
                    :placeholder="form.schema.placeholder"
                    :maxlength="form.schema.maxLength || 1024"
                    @input="onInput"
                    @blur="onInputBlur()"
                    @focus="onInputFocus"
                    @change="onChange"
                >

                <template v-if="accessed && error" #errorMessage>
                    {{ error }}
                </template>
                <template v-if="$slots.icon" #icon>
                    <slot name="icon" />
                </template>
                <template v-if="deletable && !rbacDisabled" #actionButtons>
                    <NewButton
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <FontAwesomeIcon :icon="faXmark" />
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { Component, Watch } from 'vue-property-decorator';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaString } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { isEmail } from '@evidentid/universal-framework/validation';

    import EidIcon from '../../EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '../../Form';
    import NewButton from '../../new-button/NewButton.vue';

    import AbstractJsonSchemaForm from './AbstractJsonSchemaForm';

    @Component({
        emits: [ 'change', 'delete', 'input' ],
        components: { FormInput, FormElement, FontAwesomeIcon, NewButton, EidIcon },
        inheritAttrs: false,
    })
    export default class JsonSchemaTextForm extends AbstractJsonSchemaForm<JsonFormBase<JsonSchemaString>, string> {
        private faExclamationCircle = faExclamationCircle;
        private faQuestionCircle = faQuestionCircle;
        private faXmark = faXmark;
        private inputStyle = {};
        private valueOnlyStyle = {};

        protected override getCustomError(): string | null {
            const value = this.value;
            if ((this.form.schema.minLength || 0) > value.length) {
                return this.translate('error.text.tooShort');
            } else if ((this.form.schema.maxLength || Infinity) < value.length) {
                return this.translate('error.text.tooLong');
            } else if (this.form.schema.format === 'non-email' && isEmail(value)) {
                return this.translate('error.nonEmailString.invalid', { fieldName: this.form.schema.title || 'This field' });
            }
            return null;
        }

        private get requiredElement(): boolean {
            return Boolean(this.required && (this.form.schema.minLength || this.form.schema.pattern));
        }

        private get viewValue() {
            return typeof this.value === 'string' || typeof this.value === 'number' ? `${this.value}` : '';
        }

        @Watch('valueOnly', { immediate: true })
        private onValueOnlyChange() {
            if (!this.wrapValueMode) {
                this.valueOnly ? this.setShowValueOnlyStyles() : this.setShowInputStyles();
            }
        }

        private onInput(event: InputEvent) {
            const inputValue = (event.target as HTMLInputElement).value;
            this.$emit('input', this.form.getValue(inputValue));
        }

        private onChange(event: Event): void {
            const inputValue = (event.target as HTMLInputElement).value;
            this.$emit('change', this.form.getValue(inputValue));
        }

        private setShowInputStyles() {
            this.inputStyle = { display: 'block' };
            this.valueOnlyStyle = { display: 'none' };
        }

        private setShowValueOnlyStyles() {
            this.inputStyle = { display: 'none' };
            this.valueOnlyStyle = { display: 'block' };
        }

        public mounted() {
            if (this.valueOnly) {
                return;
            }
            if (this.wrapValueMode) {
                this.viewValue ? this.setShowValueOnlyStyles() : this.setShowInputStyles();
                (this.$refs.valueOnly as any)?.addEventListener('click', this.valueOnlyClick);
            } else {
                this.setShowInputStyles();
            }
        }

        public unmounted() {
            if (this.wrapValueMode) {
                (this.$refs.valueOnly as any)?.removeEventListener('click', this.valueOnlyClick);
            }
        }

        public valueOnlyClick() {
            if (this.disabled || this.rbacDisabled) {
                return;
            }
            this.setShowInputStyles();
            this.$nextTick(() => {
                (this.$refs.input as any)?.click();
                (this.$refs.input as any)?.focus();
            });
        }

        private onInputBlur(): void {
            if (this.wrapValueMode) {
                this.viewValue ? this.setShowValueOnlyStyles() : this.setShowInputStyles();
            }
            this.touch();
        }

        private onInputFocus(): void {
            if (this.wrapValueMode) {
                this.setShowInputStyles();
            }
            this.onFocus();
        }
    }
</script>
